import { ReactElement } from 'react';
import { useForm, SubmitHandler, FormProvider } from 'react-hook-form';
import { Trans, useTranslation } from 'react-i18next';
import { type DecisionTreeFormSceneProps } from '@provider-directory/app/ui/sceneFlows';
import { SceneForm } from '@provider-directory/components/scene-form/scene-form.component';
import { AppointmentTimeFormFieldValues } from '@provider-directory/components/select-appointment-slot-form/select-appointment-form-values';
import { SelectAppointmentSlotFormRawWithData } from '@provider-directory/components/select-appointment-slot-form/select-appointment-slot-form.component';
import {
  SlotDateMode,
  useAppointment,
} from '@provider-directory/contexts/AppointmentContext';
import {
  useDecisionTree,
  getSchedulingFlowType,
} from '@provider-directory/contexts/DecisionTreeContext';
import { useNoAvailabilityScenario } from '@provider-directory/contexts/NoAvailabilityScenarioContext';
import { useProvider } from '@provider-directory/contexts/ProviderContext';
import { getIndefiniteArticle } from '@provider-directory/functions/string';
import {
  SceneSlug,
  getNavigationOptions,
  getDecisionTreeLocation,
} from '@provider-directory/models/decision-tree.model';
import { useAnalytics } from '../analytics/analytics-provider';
import { NoBreak } from '../helpers';
import { useOrgContext } from '../org-context/org-context-provider';
import { DecisionTreeContainer } from './decision-tree-container';
import { DecisionTreeHeader } from './decision-tree-header';
import { getSceneValidationByType } from './decision-tree-validations';

interface DecisionTreeFormProps extends DecisionTreeFormSceneProps {
  onNext: (slug: SceneSlug, options?: { redirectLink?: string }) => void;
  autoFocusHeading?: boolean;
}

// Returns the slot answer in ISO 8601 format
const formatSlotAnswer = (slotDate: string, slotTime: string): string => {
  const dateTimeString = `${slotDate}T${slotTime}`;

  return new Date(dateTimeString).toISOString();
};

export const DecisionTreeForm = ({
  name,
  text,
  validation,
  nextScene,
  onNext,
  autoFocusHeading,
}: DecisionTreeFormProps): ReactElement => {
  const { key, runtimeConfig } = useOrgContext();
  const { t } = useTranslation(key);
  const { updateAppointment } = useAppointment();
  const { provider } = useProvider();
  const analytics = useAnalytics();
  // ** TODO: Extract these into separate components after reorg search page
  const {
    decisionTree: { rfvId, rfvKey },
    specialtyBranch,
    setIsOpen,
    updateDecisionTree: _updateDecisionTree,
    navigateToScene: _navigateToScene,
  } = useDecisionTree();

  const { openNoRfvProvidersModal: _openNoRfvProvidersModal } =
    useNoAvailabilityScenario();

  const onClose = () => {
    setIsOpen(false);
  };

  const appointmentName = rfvKey
    ? t(`decisionTree.reasonForVisit.${rfvKey}.label`)
    : null;
  const article = appointmentName
    ? getIndefiniteArticle(appointmentName)
    : 'an';

  const methods = useForm<AppointmentTimeFormFieldValues>({
    mode: 'onSubmit',
  });
  const { handleSubmit } = methods;

  const onSubmit: SubmitHandler<AppointmentTimeFormFieldValues> = (data) => {
    if (!provider || !rfvId) return;

    const { visitTypeId } = data;

    updateAppointment({
      ...data,
      visitTypeId,
      specialtyBranch,
      reasonForVisitKey: rfvKey,
      slotDateMode: SlotDateMode.enum.selected,
    });

    analytics.dTreeAnswerSubmittedEvent({
      questionId: name,
      answer: formatSlotAnswer(data.slotDate!, data.slotTime!),
      provider,
      location: getDecisionTreeLocation(provider, data.departmentId)!,
      specialtyBranch,
      reasonForVisit: rfvKey,
      visitTypeId: visitTypeId ?? undefined,
      schedulingFlowType: getSchedulingFlowType(),
    });

    if (validation) {
      const validations = Array.isArray(validation) ? validation : [validation];
      for (const { type, failedValidationScene } of validations) {
        const validationFunc = getSceneValidationByType(type);
        const isValid = validationFunc({
          provider,
          reasonForVisit: rfvId,
          associationGroupCodes: runtimeConfig.associationGroupCodes,
        });
        if (!isValid) {
          onNext(failedValidationScene);
          return;
        }
      }
    }
    const navigateNextOptions = getNavigationOptions(nextScene);
    onNext(nextScene, navigateNextOptions);
  };

  return (
    <DecisionTreeContainer>
      {provider?.name.full ? (
        <DecisionTreeHeader autoFocus={autoFocusHeading}>
          <Trans
            t={t}
            i18nKey={text}
            components={{
              nobreak: <NoBreak />,
            }}
            values={{
              article,
              appointmentName,
              providerName: provider.name.full,
            }}
          />
        </DecisionTreeHeader>
      ) : null}
      <FormProvider {...methods}>
        <SceneForm onSubmit={handleSubmit(onSubmit)} id={name}>
          <SelectAppointmentSlotFormRawWithData
            methods={methods}
            reasonForVisit={rfvId || ''}
            onClose={onClose}
            renderContext={'decision-tree'}
          />
        </SceneForm>
      </FormProvider>
    </DecisionTreeContainer>
  );
};
